import React, { useCallback, useEffect, useState } from "react";
import { X, Sparkles, Loader2, Check, Zap, Gift, AlertTriangle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { creditsApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { PromoBanner } from "./PromoBanner";
import EmbeddedStripeCheckoutModal from "./payments/EmbeddedStripeCheckoutModal";

const fmt = (n) => Number(n || 0).toLocaleString();
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function BuyCreditsModal({ open, onClose, banner }) {
  const { user } = useAuth();
  const isAdmin = !!(user && user !== false && user.role === "admin");
  const [packages, setPackages] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buyingId, setBuyingId] = useState(null);
  const [checkout, setCheckout] = useState(null); // {clientSecret, sessionId, pkg}

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([creditsApi.packages(), creditsApi.balance()])
      .then(([pkgs, bal]) => {
        setPackages(pkgs || []);
        setBalance(bal);
      })
      .catch(() => toast.error("Couldn't load credit packages."))
      .finally(() => setLoading(false));
  }, [open]);

  const onComplete = useCallback(async () => {
    const sid = checkout?.sessionId;
    if (!sid) { setCheckout(null); return; }
    for (let i = 0; i < 6; i += 1) {
      const s = await creditsApi.status(sid).catch(() => null);
      if (s?.payment_status === "paid") {
        toast.success(`Payment received — ${fmt(checkout.credits)} credits added to your wallet!`);
        setCheckout(null);
        setBuyingId(null);
        // Refresh balance
        creditsApi.balance().then(setBalance).catch(() => {});
        window.dispatchEvent(new Event("credits-updated"));
        onClose?.();
        return;
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    toast.message("Payment still processing. Refresh in a moment.");
    setCheckout(null);
    setBuyingId(null);
  }, [checkout, onClose]);

  if (!open) return null;

  const buy = async (pkg) => {
    if (!isAdmin) return;
    setBuyingId(pkg.id);
    try {
      const origin = window.location.origin;
      const res = await creditsApi.checkout(pkg.id, origin, { embedded: true });
      if (res?.client_secret) {
        setCheckout({
          clientSecret: res.client_secret,
          sessionId: res.session_id,
          pkg,
          credits: res.credits,
          amount: res.amount,
        });
      } else if (res?.url) {
        // Fallback for older backend
        window.location.href = res.url;
      } else {
        toast.error("Could not start checkout.");
        setBuyingId(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed.");
      setBuyingId(null);
    }
  };

  return (
    <>
    <div
      data-testid="buy-credits-modal"
      className="fixed inset-0 z-[210] flex items-center justify-center bg-[#070B16]/80 backdrop-blur-md p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative px-7 pt-6 pb-5 bg-gradient-to-br from-amber-400 via-amber-400 to-yellow-300">
          <button
            onClick={onClose}
            data-testid="buy-credits-close"
            className="absolute top-4 right-4 h-8 w-8 rounded-full bg-black/10 hover:bg-black/20 flex items-center justify-center text-amber-950"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-amber-950 flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-amber-300" />
            </div>
            <div>
              <div className="text-amber-950 font-bold text-[18px] leading-tight">Buy AI Credits</div>
              <div className="text-amber-900/80 text-[12.5px]">
                {balance ? (
                  <>You have <strong>{fmt(balance.totalDisplay)}</strong> credits left</>
                ) : (
                  "Top up your AI usage balance"
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="px-7 py-6">
          {banner && (
            <div data-testid="buy-credits-low-banner" className="mb-4 flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
              <AlertTriangle className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />
              <div className="text-[13px] text-rose-700 font-medium leading-snug">{banner}</div>
            </div>
          )}
          {balance?.promo && <PromoBanner promo={balance.promo} className="mb-4" />}
          {!isAdmin && (
            <div data-testid="buy-credits-admin-only-note" className="mb-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-[12.5px] text-amber-700 font-medium">
              <ShieldAlert className="h-4 w-4 shrink-0" /> Only an Admin can complete a purchase. Ask your account Admin to buy more credits.
            </div>
          )}
          {loading ? (
            <div className="py-12 flex items-center justify-center text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading packages…
            </div>
          ) : packages.length === 0 ? (
            <div className="py-12 text-center text-[13.5px] text-slate-400" data-testid="buy-credits-empty">
              No credit packages are available right now. Please check back soon.
            </div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-3.5">
              {packages.map((pkg, i) => {
                const featured = i === 1; // middle pack highlighted
                const bonusPct = pkg.credits > 0 && pkg.bonusCredits > 0 ? Math.round((pkg.bonusCredits / pkg.credits) * 100) : 0;
                const promoPct = balance?.promo?.promoDiscountPercent;
                const discountedPrice = promoPct
                  ? Math.max(0.50, Math.round(pkg.priceUsd * (1 - promoPct / 100) * 100) / 100)
                  : null;
                return (
                  <div
                    key={pkg.id}
                    data-testid={`credit-package-${pkg.id}`}
                    className={`relative rounded-2xl border p-4 flex flex-col ${
                      featured ? "border-amber-400 ring-2 ring-amber-300/50 bg-amber-50/40" : "border-slate-200 bg-white"
                    }`}
                  >
                    {bonusPct > 0 && (
                      <span className="absolute -top-2.5 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10.5px] font-bold">
                        <Gift className="h-3 w-3" /> +{bonusPct}%
                      </span>
                    )}
                    <div className="text-[13px] font-bold text-slate-900">{pkg.name}</div>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="text-[26px] font-extrabold text-slate-900 tabular-nums">{fmt(pkg.totalCredits)}</span>
                      <span className="text-[12px] text-slate-500 font-medium">credits</span>
                    </div>
                    {pkg.bonusCredits > 0 && (
                      <div className="text-[11.5px] text-emerald-600 font-medium -mt-0.5">
                        {fmt(pkg.credits)} + {fmt(pkg.bonusCredits)} bonus
                      </div>
                    )}
                    <div className="mt-2 flex items-baseline gap-1.5" data-testid={`credit-package-price-${pkg.id}`}>
                      {discountedPrice !== null ? (
                        <>
                          <span className="text-[15px] font-bold text-emerald-600">{money(discountedPrice)}</span>
                          <span className="text-[12.5px] text-slate-400 line-through">{money(pkg.priceUsd)}</span>
                        </>
                      ) : (
                        <span className="text-[15px] font-bold text-slate-700">{money(pkg.priceUsd)}</span>
                      )}
                    </div>
                    <button
                      onClick={() => buy(pkg)}
                      disabled={!!buyingId || !isAdmin}
                      title={!isAdmin ? "Only an Admin can complete a purchase" : undefined}
                      data-testid={`buy-package-${pkg.id}`}
                      className={`mt-3 h-9 rounded-lg text-[13px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed ${
                        featured ? "bg-amber-500 hover:bg-amber-600 text-amber-950" : "bg-slate-900 hover:bg-slate-800 text-white"
                      }`}
                    >
                      {buyingId === pkg.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                      {buyingId === pkg.id ? "Redirecting…" : "Purchase"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-5 flex items-center gap-2 text-[11.5px] text-slate-400">
            <Check className="h-3.5 w-3.5 text-emerald-500" /> Purchased credits never expire. Secure checkout via Stripe.
          </div>
        </div>
      </div>
    </div>
    {checkout && (
      <EmbeddedStripeCheckoutModal
        clientSecret={checkout.clientSecret}
        title={checkout.pkg?.name || "Buy credits"}
        subtitle={`${fmt(checkout.credits)} credits · ${money(checkout.amount)}`}
        onComplete={onComplete}
        onClose={() => { setCheckout(null); setBuyingId(null); }}
        testid="credits-embedded-checkout"
      />
    )}
    </>
  );
}
